import { useRouter } from '@gef-ui/router-core';
import Badge from '@mui/material/Badge';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { alpha, styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import useBetweenMdXl from '../../../hooks/useBetweenMdXl';
import { APPBAR_ICON_SIDE, NAVBAR } from '../../sizeConstants';
import { passSx, useDefaultComponentValue } from '../../utils';
import { useIsExpandMode } from '../SidebarContext';
import { useRegisterPath } from '../submenuContext';
const ListItemIconStyle = styled(ListItemIcon, {
    shouldForwardProp: (prop)=>prop !== 'isActive'
})(({ theme })=>({
        width: APPBAR_ICON_SIDE,
        height: APPBAR_ICON_SIDE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& svg': {
            width: '100%',
            height: '100%'
        },
        variants: [
            {
                props: ({ isActive })=>isActive,
                style: {
                    color: theme.palette.primary.main
                }
            }
        ]
    }));
const ListItemTextStyle = styled(ListItemText)(({ theme })=>({
        whiteSpace: 'nowrap',
        transition: theme.transitions.create([
            'width',
            'opacity'
        ], {
            duration: theme.transitions.duration.shorter
        })
    }));
const ListItemStyle = styled(ListItemButton, {
    shouldForwardProp: (prop)=>prop !== 'activeRoot' && prop !== 'activeSub' && prop !== 'subItem'
})(({ theme })=>({
        ...theme.typography.body1,
        position: 'relative',
        height: NAVBAR.DASHBOARD_ITEM_ROOT_HEIGHT,
        textTransform: 'capitalize',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1.5),
        marginBottom: theme.spacing(0.5),
        color: theme.palette.text.secondary,
        borderRadius: theme.shape.borderRadius,
        variants: [
            {
                props: ({ activeRoot })=>activeRoot,
                style: {
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)
                }
            },
            {
                props: ({ activeSub })=>activeSub,
                style: {
                    color: theme.palette.text.primary
                }
            },
            {
                props: ({ subItem })=>subItem,
                style: {
                    height: NAVBAR.DASHBOARD_ITEM_SUB_HEIGHT
                }
            }
        ]
    }));
const COMPONENT_NAME = 'SidebarMenuItem';
const getPathName = (location)=>location?.asPath ?? location?.pathname;
const SidebarMenuItem = /*#__PURE__*/ forwardRef((inProps, ref)=>{
    const props = useDefaultComponentValue({
        inProps,
        name: COMPONENT_NAME
    });
    const { to, href, icon, title, children, active, activeProp = 'activeRoot', badgeContent, BadgeProps = {}, ...other } = props;
    const betweenMdXl = useBetweenMdXl();
    const isExpandMode = useIsExpandMode();
    useRegisterPath(to || href);
    const location = useRouter()?.useLocation();
    const isActive = active || to && getPathName(location) === to || href && getPathName(location) === href;
    const listItemProps = {
        [activeProp]: isActive,
        to,
        href,
        ...other
    };
    // sadly this line overrides ButtonBase LinkComponent https://github.com/mui/material-ui/blob/master/packages/mui-material/src/ListItemButton/ListItemButton.js#L184
    // so href to have button component which will be automatically converted to LinkComponent in theme
    const workaroundComponent = listItemProps.component ? listItemProps.component : to || href ? 'button' : undefined;
    if (betweenMdXl && !isExpandMode) {
        return /*#__PURE__*/ React.createElement(Tooltip, {
            title: title,
            placement: "right",
            slotProps: {
                popper: {
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [
                                    0,
                                    9
                                ]
                            }
                        }
                    ]
                }
            }
        }, /*#__PURE__*/ React.createElement(ListItemStyle, {
            ...listItemProps,
            sx: [
                {
                    justifyContent: 'center',
                    p: 0,
                    mr: 0
                },
                ...passSx(listItemProps.sx)
            ],
            component: workaroundComponent,
            ref: ref
        }, /*#__PURE__*/ React.createElement(Badge, {
            badgeContent: badgeContent,
            slotProps: {
                badge: {
                    sx: {
                        top: -3,
                        right: -2
                    }
                }
            },
            color: "primary",
            ...BadgeProps
        }, icon && /*#__PURE__*/ React.createElement(ListItemIconStyle, {
            isActive: isActive,
            sx: {
                p: 0,
                mr: 0
            }
        }, icon)), children));
    }
    return /*#__PURE__*/ React.createElement(ListItemStyle, {
        ...listItemProps,
        component: workaroundComponent,
        ref: ref
    }, icon && /*#__PURE__*/ React.createElement(ListItemIconStyle, {
        isActive: isActive
    }, icon), /*#__PURE__*/ React.createElement(ListItemTextStyle, {
        disableTypography: true,
        primary: title
    }), badgeContent && /*#__PURE__*/ React.createElement(Badge, {
        badgeContent: badgeContent,
        color: "primary",
        sx: {
            mr: 1
        },
        ...BadgeProps
    }), children);
});
SidebarMenuItem.displayName = COMPONENT_NAME;
SidebarMenuItem.propTypes = {
    BadgeProps: PropTypes.object,
    active: PropTypes.bool,
    activeProp: PropTypes.string,
    badgeContent: PropTypes.node,
    children: PropTypes.node,
    href: PropTypes.string,
    icon: PropTypes.node,
    title: PropTypes.node,
    to: PropTypes.string
};
export default SidebarMenuItem;
